// @flow
import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import io from 'socket.io-client';
import { updateOrderStatusRealtime, setSocketConnection, addRealtimeStatus, addMachineMessage } from '../actions/orders';

type SocketState = {
  connected: boolean,
  socket: any,
  error: string | null,
  lastUpdate: Date | null
};

const useOrderSocket = (orderId: string, machineImei?: string) => {
  const [socketState, setSocketState] = useState<SocketState>({
    connected: false,
    socket: null,
    error: null,
    lastUpdate: null
  });

  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const { sessionId } = useSelector((state) => state.auth || {});

  const connectSocket = useCallback(() => {
    try {
      // Connect to WebSocket server (URL from environment variable)
      const websocketUrl = process.env.REACT_APP_LIVE_URL || 'ws://localhost:4200';
      const socket = io(websocketUrl, {
        transports: ['websocket'],
        timeout: 5000,
        forceNew: true
      });

      socket.on('connect', () => {
        console.log('🔗 Connected to order tracking server');
        setSocketState(prev => ({ ...prev, connected: true, error: null }));
        dispatch(setSocketConnection(true));

        // Subscribe to specific order updates
        socket.emit('subscribeToOrder', orderId);

        // Subscribe to machine-level messages on IMEI channel if IMEI is available
        if (machineImei) {
          console.log(`📱 Subscribing to machine messages on IMEI ${machineImei}`);
          socket.emit('subscribeToMachine', machineImei);

          // Send open-door message on IMEI channel
          console.log(`🚪 Sending open-door message on channel ${machineImei}`);
          socket.emit('open-door', {
            channel: machineImei,
            data: {
              orderId: orderId
            },
            source: "webapp",
            timestamp: new Date().toISOString()
          });
        }
      });

      socket.on('orderStatus', (data) => {
        console.log('📡 Received real-time status:', data);

        setSocketState(prev => ({ ...prev, lastUpdate: new Date() }));

        const statusUpdate = {
          orderId: data.order_id || orderId,
          status: data.server_status, // Map server_status to status
          timestamp: data.timestamp || new Date().toISOString(),
          metadata: {
            server_response: data.server_response, // Keep server_response as string
            id: data.id
          },
          machineId: data.machine_id || 'unknown'
        };

        console.log('📤 Dispatching status update:', statusUpdate);

        // Dispatch to Redux for state management
        dispatch(updateOrderStatusRealtime(statusUpdate));

        // Also add to the realtimeStatuses array for ProcessOrder component
        dispatch(addRealtimeStatus(statusUpdate));
      });

      socket.on('machineMessage', (data) => {
        console.log('🤖 Received machine message:', data);

        setSocketState(prev => ({ ...prev, lastUpdate: new Date() }));

        const machineMessage = {
          id: data.id,
          imei: data.imei || machineImei || 'unknown',
          messageType: data.message_type || 'info',
          timestamp: data.timestamp || new Date().toISOString(),
          priority: data.priority || 'normal',
          display: {
            header: data.display_header || data.display?.header || '',
            subHeader: data.display_sub_header || data.display?.sub_header || '',
            showOnUI: data.display_show_on_ui !== 'false' && data.display?.show_on_ui !== false,
            autoDismiss: data.display_auto_dismiss === 'true' || data.display?.auto_dismiss || false,
            dismissAfter: parseInt(data.display_dismiss_after) || data.display?.dismiss_after || 30000
          },
          metadata: data.metadata || {}
        };

        console.log('📤 Dispatching machine message:', machineMessage);

        // Dispatch to Redux for state management
        dispatch(addMachineMessage(machineMessage));
      });

      socket.on('error', (error) => {
        console.error('🔴 Socket error:', error);
        setSocketState(prev => ({ ...prev, error: error.message }));
      });

      socket.on('disconnect', () => {
        console.log('🔌 Disconnected from order tracking server');
        setSocketState(prev => ({ ...prev, connected: false }));
        dispatch(setSocketConnection(false));
      });

      socket.on('connect_error', (error) => {
        console.error('🔴 Connection failed:', error);
        setSocketState(prev => ({ ...prev, error: 'Connection failed', connected: false }));
      });

      socketRef.current = socket;
      setSocketState(prev => ({ ...prev, socket }));

    } catch (error) {
      console.error('🔴 Socket initialization error:', error);
      setSocketState(prev => ({ ...prev, error: error.message }));
    }
  }, [orderId, machineImei, dispatch]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocketState(prev => ({ ...prev, connected: false, socket: null }));
      dispatch(setSocketConnection(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (orderId && sessionId) {
      connectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [orderId, machineImei, sessionId, connectSocket, disconnectSocket]);

  return {
    connected: socketState.connected,
    error: socketState.error,
    lastUpdate: socketState.lastUpdate,
    reconnect: connectSocket
  };
};

export default useOrderSocket;
